import { useEffect, useMemo, useState } from "react";
import { Select, Option } from "@material-tailwind/react";
import {
  Card,
  Typography
} from "@material-tailwind/react";
import moment from "moment";
import { get } from "lodash";
import { Breadcrumbs } from "components/breadcrumbs";
import ApiService from "utills/ApiService";
import { PHONE_CODE } from "utills/constants";
import { FullScreenLoading } from "components/fullScreenLoading";
import useWindowWidth from "utills/InitWidthDevice";
import { useParse, useTranslation } from "@refinedev/core";
import toast from "react-hot-toast";
import './edit.scss';
import ConvertCurrency from "utills/convertCurrency";
import ReactSelect from 'react-select';
import { convertCurrency } from "utills/helpers";

const OrderForm = (props: any) => {
  const { translate, isMobile, language, orderID, token } = props;
  const TABLE_HEAD = [translate('no'), translate('ticket-name'), translate('quantity'), translate('price'), translate('discount'), translate('total'), translate('used-code')];
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null as any);
  const [error, setError] = useState(
    {
      buyer: {
        name: '',
        email: '',
        phone: '',
        phone_area_code: ''
      },
      tax: {
        company_name: '',
        recipient_name: '',
        company_address: '',
        recipient_phone: '',
        company_tax_code: '',
        recipient_address: ''
      }
    }
  );
  const [addVAT, setAddVAT] = useState(true);
  const [symbolCurrency, setSymbolCurrency] = useState('đ');
  const [currentRates, setCurrentRates] = useState(0);

  const PHONE_CODE_OPTIONS = useMemo(() => {
    return PHONE_CODE.map((pc) => {
      return {
        label: pc.label,
        value: pc.code,
      };
    });
  }, [PHONE_CODE]);

  useEffect(() => {
    if (orderID !== '') {
      fetchOrderDetail();
    }
  }, [orderID]);

  useEffect(() => {
    if (orderDetail && orderDetail?.rates && orderDetail?.rates?.rates && orderDetail?.rates?.rates?.VND) {
      setCurrentRates(orderDetail?.rates?.rates?.VND);
    }
  }, [orderDetail]);

  useEffect(() => {
    const wsCurrency = localStorage.getItem('wsCurrency');

    if (wsCurrency && wsCurrency.toLowerCase() === 'usd') {
      setSymbolCurrency('$');
    } else {
      setSymbolCurrency('đ');
    }
  }, []);

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`orders/${orderID}`, token);

      if (result) {
        if (!result.tax) {
          result.tax = {
            company_name: '',
            recipient_name: '',
            company_address: '',
            recipient_phone: '',
            company_tax_code: '',
            recipient_address: '',
            recipient_phone_area_code: ''
          }
        }

        setOrderDetail(result);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const getRequiredErrorMessage = (key: string) => {
    if (key === "name") return `${translate('error-field-require')} ${translate('buyer-information')}`;
    if (key === "company_name") return `${translate('error-field-require')} ${translate('company-name')}`;
    if (key === "company_address") return `${translate('error-field-require')} ${translate('company-address')}`;
    if (key === "company_tax_code") return `${translate('error-field-require')} ${translate('company-tax')}`;
    if (key === "recipient_name") return `${translate('error-field-require')} ${translate('recipient-name')}`;

    return translate('error-field-require');
  }

  const handleChangeValue = (value: string, group: string, key: string) => {
    const order_temp = { ...orderDetail } as any;
    const errs = { ...error } as any;

    order_temp[group][key] = value;

    errs[group][key] = (value === '') ? getRequiredErrorMessage(key) : '';

    setError({ ...errs });
    setOrderDetail({ ...order_temp });

    if (key === 'email' || key === 'recipient_address') {
      validateEmail(value, group, key)
    }

    if (key.includes('phone') && !key.includes('area_code')) {
      validatePhone(value, group, key);
    }
  }

  const validateEmail = (value: string, group: string, key: string) => {
    let err = '';

    let requiredMsgInfo = '';
    if (group === "buyer") requiredMsgInfo = translate('buyer-email')
    if (group === "tax") requiredMsgInfo = translate('recipient-email');

    if (!value) {
      err = `${translate('error-field-require')} ${requiredMsgInfo}`;
    } else {
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
        err = translate('wrong-format-email');
      }
    }
    const errs = { ...error } as any;

    if (key === 'email') {
      errs.buyer.email = err;
    } else {
      errs.tax.recipient_address = err;
    }
    setError({ ...errs });
  }

  const validatePhone = (value: string, group: string, key: string) => {
    let err = '';

    let requiredMsgInfo = '';
    if (group === "buyer") requiredMsgInfo = translate('phone-number')
    if (group === "tax") requiredMsgInfo = translate('recipient-phone');

    if (!value) {
      err = `${translate('error-field-require')} ${requiredMsgInfo}`;
    } else {
      if (!/^\d{5,15}$/i.test(value)) {
        err = translate('wrong-format-phone');
      }
    }

    const errs = { ...error } as any;

    if (key === 'phone') {
      errs.buyer.phone = err;
    } else {
      errs.tax[key] = err;
    }

    setError({ ...errs });
  }

  const collectErrors = (obj: any, parentKey = "", errors: string[] = []) => {
    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      
      if (typeof value === "object" && value !== null) {
        collectErrors(value, fullKey, errors);
      } else if (typeof value === "string" && value.trim() !== "") {
        errors.push(`${value}`);
      }
    });
    return errors;
  }

  const handleUpdateOrder = async () => {
    setLoading(true);

    const errs = { ...error } as any;
    const keys_vat = Object.keys(errs.tax);
    const keys_buyer = Object.keys(errs.buyer);
    let check_data = true;

    if (orderDetail.is_vat === "0") {
      if (!addVAT) {
        keys_vat.forEach((item: any) => {
          if (!orderDetail.tax[item]) {
            errs.tax[item] = translate('error-field-require');
          } else {
            errs.tax[item] = (orderDetail.tax[item] === '') ? translate('error-field-require') : '';
          }
        });
      }
    }

    keys_vat.forEach((item: any) => {
      if (errs.tax[item] !== "") {
        check_data = false;
      }
    });

    keys_buyer.forEach((item: any) => {
      if (errs.buyer[item] !== "") {
        check_data = false;
      }
    });

    setError({ ...errs });

    if (!check_data) {
      const errorList: string[] = collectErrors(error);

      errorList?.map((e: string) => {
        toast.error(e);
      });
    }

    if (check_data) {
      const submitData = {
        "buyer_name": orderDetail.buyer.name,
        "buyer_email": orderDetail.buyer.email,
        "buyer_phone": orderDetail.buyer.phone,
        "buyer_phone_area_code": orderDetail.buyer.phone_area_code,
        "company_name": (orderDetail.tax.company_name === '') ? null : orderDetail.tax.company_name,
        "company_address": (orderDetail.tax.company_address === '') ? null : orderDetail.tax.company_address,
        "company_tax_code": (orderDetail.tax.company_tax_code === '') ? null : orderDetail.tax.company_tax_code,
        "recipient_name": (orderDetail.tax.recipient_name === '') ? null : orderDetail.tax.recipient_name,
        "recipient_address": (orderDetail.tax.recipient_address === '') ? null : orderDetail.tax.recipient_address,
        "recipient_phone": (orderDetail.tax.recipient_phone === '') ? null : orderDetail.tax.recipient_phone,
        "recipient_phone_area_code": (orderDetail.tax.recipient_phone_area_code === '') ? null : orderDetail.tax.recipient_phone_area_code
      }

      try {
        const res = await ApiService.put(`orders/${orderID}`, submitData, token);

        if (res) {
          toast.success(`${translate('update-success') ?? "Update successfully"}`);

          setTimeout(() => {
             window.location.href = `/orders/${orderID}`;
          }, 3000);
        }

      } catch (e: any) {
        toast.error(e.message);
      }
    }

    setLoading(false);
  }

  const getPaymentMethod = (method: string) => {
    const payment_method = method.toLowerCase();
    switch (payment_method) {
      case 'atm':
        return translate('atm');
      case 'bank_transfer':
        return translate('bank-transfer');
      case 'credit':
        return translate('credit');
      case 'partner_vnpay':
        return 'Partner VNPay';
      case 'vn_pay_b':
        return 'VNPAY';
      case 'vnpay':
        return 'QR Code';
      case 'zalopay':
        return 'ZaloPay';
      case 'viet_qr':
        return 'Viet QR';
      case 'momo':
        return translate('momo');
      case 'voucher':
        return translate('coupon');
      case 'shipcode':
        return 'Ship Code';
      case 'free':
        return translate('free-ticket');
      case 'bnpl':
        return translate('bnpl');
      case 'viettel_money':
        return translate('viettel_money');
      case 'v_atm':
        return translate('v_atm');
      case 'v_credit':
        return translate('v_credit');
      case 'payoo_atm':
        return translate('payoo_atm');
      case 'payoo_credit':
        return translate('payoo_credit');
      case 'onepay_viet_qr':
        return translate('onepay_viet_qr');
      case 'stripe':
        return translate('stripe-payment');
      default:
        return method;
    }
  }

  return (
    <div className="order-form relative">
      {
        orderDetail &&
        <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full grid grid-cols-1 gap-[16px]`}>
          {
            orderDetail.buyer &&
            <div className="w-full bg-white rounded-[4px]">
              <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full border-b border-[#c5ccd2] flex items-center justify-between`}>
                <div className="text-16 font-700">{translate('buyer-information')}</div>
              </div>

              <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} grid grid-cols-1 gap-[16px]`}>
                <div className={`${isMobile ? 'w-[100%]' : 'w-[50%]'} mb-[24px]`}>
                  <div className="text-[14px] font-[500] text-[#262626] flex mb-[4px]">
                    <span>{translate('full-name')}</span>

                    <img src="/images/icons/icon-required.svg" className="ml-[4px]" alt="icon-required" />
                  </div>

                  <input onChange={(e: any) => handleChangeValue(e.target.value, 'buyer', 'name')} value={orderDetail.buyer.name} type="text" className="w-[100%] h-[48px] px-[16px] rounded-[4px] border-[1px] border-[#C5CCD2]" />

                  {
                    error.buyer.name !== '' &&
                    <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.buyer.name}</div>
                  }
                </div>

                <div className={`${isMobile ? 'w-[100%]' : 'w-[50%]'} mb-[24px]`}>
                  <div className="text-[14px] font-[500] text-[#262626] flex mb-[4px]">
                    <span>{translate('Email')}</span>

                    <img src="/images/icons/icon-required.svg" className="ml-[4px]" alt="icon-required" />
                  </div>

                  <input onChange={(e: any) => handleChangeValue(e.target.value, 'buyer', 'email')} value={orderDetail.buyer.email} type="text" className="w-[100%] h-[48px] px-[16px] rounded-[4px] border-[1px] border-[#C5CCD2]" />

                  {
                    error.buyer.email !== '' &&
                    <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.buyer.email}</div>
                  }
                </div>

                <div className={`${isMobile ? 'w-[100%]' : 'w-[50%]'} mb-[24px]`}>
                  <div className="text-[14px] font-[500] text-[#262626] flex mb-[4px]">
                    <span>{translate('contact-phone')}</span>

                    <img src="/images/icons/icon-required.svg" className="ml-[4px]" alt="icon-required" />
                  </div>

                  <div className="w-[100%] h-[48px] rounded-[4px] flex">
                    {/* <div className="select-phone-code">
                      <Select
                        value={orderDetail.buyer.phone_area_code}
                        onChange={(val: any) => handleChangeValue(val, 'buyer', 'phone_area_code')}
                        placeholder=""
                        onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                        onResizeCapture={() => { }}
                        className="border-none"
                      >
                        {
                          PHONE_CODE.map((item: any, index: number) => {
                            return (
                              <Option key={`${item.value}_${index}`}
                                value={item.code}>{item.value} | +{item.code}</Option>
                            )
                          })
                        }
                      </Select>
                    </div> */}

                    <ReactSelect
                      className="w-[160px]"
                      styles={{
                        control: (base) => ({
                          ...base,
                          height: "48px",
                          minHeight: "48px",
                          // border: "none",
                          border: "1px solid #C5CCD2",
                          borderRadius: 0,
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          borderTopLeftRadius: "4px",
                          borderBottomLeftRadius: "4px",
                        }),
                        input: (base) => ({
                          ...base,
                          "input:focus": {
                            boxShadow: "none",
                          },
                        }),
                      }}
                      value={
                        PHONE_CODE_OPTIONS?.find((pc) => {
                          return pc.value === orderDetail.buyer.phone_area_code;
                        })
                      }
                      options={PHONE_CODE_OPTIONS}
                      onChange={(selected) => {
                        if (selected?.value) handleChangeValue(selected?.value, 'buyer', 'phone_area_code')
                      }}
                    />

                    <input value={orderDetail.buyer.phone} onChange={(e: any) => handleChangeValue(e.target.value, 'buyer', 'phone')} type="text"
                            className="border-[1px] border-l-0 focus:border-l focus:outline-none border-[#C5CCD2] w-[calc(100%-160px)]
                                        rounded-r-[4px]"
                            autoComplete="off" placeholder={translate('phone-number')}
                    />
                    
                  </div>

                  {
                    error.buyer.phone !== '' &&
                    <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.buyer.phone}</div>
                  }
                </div>
              </div>
            </div>
          }

          <div className="w-full bg-white rounded-[4px]">
            <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full border-b border-[#c5ccd2] flex items-center justify-between`}>
              <div className="text-16 font-700">{translate('order-information')}</div>
            </div>

            <div className={`${isMobile ? 'grid grid-cols-1 gap-[12px] p-[16px]' : 'grid grid-cols-2 gap-[16px] p-[24px]'} w-full`}>
              <div className="w-full flex items-center gap-x-[12px]">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('order-code')}</div>
                <div className="flex-1 text-14 text-brand-500 font-600">{orderDetail.code}</div>
              </div>

              <div className="w-full flex items-center gap-x-[12px]">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('time-order')}</div>
                <div className="flex-1 text-14">{moment(orderDetail.created_at).format('DD/MM/YYYY HH:mm:ss')}</div>
              </div>

              <div className="w-full flex items-center gap-x-[12px]">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('order-amount')}</div>
                <div className="flex-1 text-14 text-brand-500 font-600">{symbolCurrency} {convertCurrency(orderDetail.grand_total, orderDetail?.currency, currentRates)}</div>
              </div>

              {
                orderDetail.purchased_at && orderDetail.purchased_at !== "1970-01-01 16:00:00" &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('time-order-success')}</div>
                  <div className="flex-1 text-14">{moment(orderDetail.purchased_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                </div>
              }

              <div className="w-full flex items-center gap-x-[12px]">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('order-status')}</div>
                <div className="flex-1 text-14">
                  {
                    orderDetail.order_status === 'doi_thanh_toan' &&
                    <span className="status pending">{translate('order-pending')}</span>
                  }

                  {
                    orderDetail.order_status === 'dat_hang_thanh_cong' &&
                    <span className="status success">{translate('order-success')}</span>
                  }

                  {
                    orderDetail.order_status === 'da_huy' &&
                    <span className="status canceled">{translate('order-canceled')}</span>
                  }

                  {
                    orderDetail.order_status === 'hoan_tien' &&
                    <span className="status refund">{translate('order-refund')}</span>
                  }
                </div>
              </div>

              {
                orderDetail.order_status && orderDetail.order_status === 'da_huy' &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('time-order-fail')}</div>
                  {
                    (orderDetail.expired_payment_at && orderDetail.expired_payment_at !== "1970-01-01 16:00:00") ?
                      <div className="flex-1 text-14">{moment(orderDetail.expired_payment_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                      :
                      <div className="flex-1 text-14">{moment(orderDetail.canceled_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                  }
                </div>
              }

              {
                orderDetail.transactions && orderDetail.transactions.length > 0 &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('payment-method')}</div>
                  <div className="flex-1 text-14">{getPaymentMethod(get(orderDetail.transactions[0], `payment_method_name_${language}`, ''))}</div>
                </div>
              }
              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('payment-unit')}</div>
                <div className="flex-1 uppercase">{orderDetail?.currency}</div>
              </div>

              {
                orderDetail.event &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('event-name')}</div>
                  <div className="flex-1 text-14">{get(orderDetail.event, `name_${language}`, '')}</div>
                </div>
              }

              {
                orderDetail.event && orderDetail.event.event_type && orderDetail.event.event_type === 'sports' &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('group-name')}</div>
                  {
                    orderDetail.extra && orderDetail.extra.checkout_data && orderDetail.extra.checkout_data.club_name && orderDetail.extra.checkout_data.club_name !== '' ?
                      <div className="flex-1 text-14">{orderDetail.extra.checkout_data.club_name}</div>
                      :
                      <div className="flex-1 text-14"></div>
                  }
                </div>
              }

              {
                orderDetail.event && orderDetail.event.event_type && orderDetail.event.event_type === 'attractions' &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('e-ticket')}</div>
                  {
                    orderDetail.ticket_use_for_date && orderDetail.ticket_use_for_date !== '' ?
                      <div className="flex-1 text-14">{moment(orderDetail.ticket_use_for_date).format('DD/MM/YYYY')}</div>
                      :
                      <div className="flex-1 text-14"></div>
                  }
                </div>
              }

              {
                (orderDetail?.auth_identifier) &&
                <div className="w-full flex items-center gap-x-[12px]">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500 text-14`}>{translate('private_flow')}</div>
                  <div className="flex-1 text-14">{orderDetail?.auth_identifier}</div>
                </div>
              }


            </div>
          </div>

          <div className="w-full bg-white rounded-[4px]">
            <div className="flex items-center justify-between w-[100%] h-[64px] px-[24px] border-b-[1px] border-b-[#D9E1E9]">
              <div className="body__item__header__label text-[16px] font-bold text-[#262626]">{translate('invoice-information')}</div>

              {
                (orderDetail.is_vat === "0") &&
                <>
                  {
                    addVAT ?
                      <div onClick={() => setAddVAT(false)} className="w-[125px] h-[40px] border-[1px] border-[#A6A1FD] rounded-[4px] flex items-center justify-center cursor-pointer">
                        <img src="/images/icons/icon-add-vat.svg" className="mr-[4px]" alt="icon-add-vat" />
                        <div className="text-[14px] font-[400] text-[#081774]">{translate('add-vat')}</div>
                      </div>
                      :
                      <div onClick={() => setAddVAT(true)} className="w-[125px] h-[40px] border-[1px] border-[#A6A1FD] rounded-[4px] flex items-center justify-center cursor-pointer">
                        <img src="/images/icons/icon-delete.svg" className="mr-[4px]" alt="icon-add-vat" />
                        <div className="text-[14px] font-[400] text-[#081774]">{translate('btn-cancel')}</div>
                      </div>
                  }
                </>
              }
            </div>

            {
              (orderDetail.is_vat === "1" || !addVAT) &&
              <div className="w-[100%] p-[24px] bg-white">
                <div className={`w-[100%] flex items-center justify-between mb-[24px] ${isMobile && 'flex-col'}`}>
                  <div className={`${isMobile ? 'w-[100%] mb-[24px]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('company-name')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <input onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'company_name')} value={orderDetail?.tax?.company_name} className="w-[100%] h-[48px] border-[1px] border-[#C5CCD2] rounded-[4px] px-[16px]" placeholder={translate('company-name')} />

                    {
                      error.tax.company_name !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.company_name}</div>
                    }
                  </div>

                  <div className={`${isMobile ? 'w-[100%]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('recipient-name')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <input onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'recipient_name')} value={orderDetail?.tax?.recipient_name} className="w-[100%] h-[48px] border-[1px] border-[#C5CCD2] rounded-[4px] px-[16px]" placeholder={translate('recipient-name')} />

                    {
                      error.tax.recipient_name !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.recipient_name}</div>
                    }
                  </div>
                </div>

                <div className={`w-[100%] flex items-center justify-between mb-[24px] ${isMobile && 'flex-col'}`}>
                  <div className={`${isMobile ? 'w-[100%] mb-[24px]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('company-address')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <input onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'company_address')} value={orderDetail?.tax?.company_address} className="w-[100%] h-[48px] border-[1px] border-[#C5CCD2] rounded-[4px] px-[16px]" placeholder={translate('company-address')} />

                    {
                      error.tax.company_address !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.company_address}</div>
                    }
                  </div>

                  <div className={`${isMobile ? 'w-[100%]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('recipient-phone')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <div className="w-[100%] h-[48px] rounded-[4px] flex">
                      {/* <div className="select-phone-code">
                        <Select
                          value={(orderDetail?.tax?.recipient_phone_area_code && orderDetail?.tax?.recipient_phone_area_code !== '') ? orderDetail?.tax?.recipient_phone_area_code : '84'}
                          onChange={(val: any) => handleChangeValue(val, 'tax', 'recipient_phone_area_code')}
                          placeholder=""
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          className="border-none"
                        >
                          {
                            PHONE_CODE.map((item: any, index: number) => {
                              return (
                                <Option key={`${item.value}_${index}`}
                                  value={item.code}>{item.value} | +{item.code}</Option>
                              )
                            })
                          }
                        </Select>
                      </div> */}

                      <ReactSelect
                        className="w-[160px]"
                        styles={{
                          control: (base) => ({
                            ...base,
                            height: "48px",
                            minHeight: "48px",
                            // border: "none",
                            border: "1px solid #C5CCD2",
                            borderRadius: 0,
                            boxShadow: "none",
                            backgroundColor: "transparent",
                            borderTopLeftRadius: "4px",
                            borderBottomLeftRadius: "4px",
                          }),
                          input: (base) => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                        }}
                        value={
                          PHONE_CODE_OPTIONS?.find((pc) => {
                            return pc.value === orderDetail?.tax?.recipient_phone_area_code;
                          })
                        }
                        options={PHONE_CODE_OPTIONS}
                        onChange={(selected) => {
                          if (selected?.value) handleChangeValue(selected?.value, 'tax', 'recipient_phone_area_code')
                        }}
                      />

                      <input value={orderDetail?.tax?.recipient_phone} onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'recipient_phone')} type="text"
                              className="border-[1px] border-l-0 focus:border-l focus:outline-none border-[#C5CCD2] w-[calc(100%-160px)]
                                        rounded-r-[4px]"
                              autoComplete="off" placeholder={translate('phone-number')}
                      />
                    </div>

                    {
                      error.tax.recipient_phone !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.recipient_phone}</div>
                    }
                  </div>
                </div>

                <div className={`w-[100%] flex items-center justify-between mb-[24px] ${isMobile && 'flex-col'}`}>
                  <div className={`${isMobile ? 'w-[100%] mb-[24px]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('company-tax')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <input onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'company_tax_code')} value={orderDetail?.tax?.company_tax_code} className="w-[100%] h-[48px] border-[1px] border-[#C5CCD2] rounded-[4px] px-[16px]" placeholder={translate('company-tax')} />

                    {
                      error.tax.company_tax_code !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.company_tax_code}</div>
                    }
                  </div>

                  <div className={`${isMobile ? 'w-[100%]' : 'w-[49%]'}`}>
                    <div className="flex mb-[4px]">
                      <span className="text-[14px] text-[#262626] font-[500] mr-[4px]">{translate('Email')}</span>

                      <img src="/images/icons/icon-required.svg" alt="icon-required" />
                    </div>

                    <input onChange={(e: any) => handleChangeValue(e.target.value, 'tax', 'recipient_address')} value={orderDetail?.tax?.recipient_address} className="w-[100%] h-[48px] border-[1px] border-[#C5CCD2] rounded-[4px] px-[16px]" placeholder={translate('recipient-name')} />

                    {
                      error.tax.recipient_address !== '' &&
                      <div className="text-[#ED4927] text-[14px] mt-[5px]">{error.tax.recipient_address}</div>
                    }
                  </div>
                </div>
              </div>
            }

          </div>

          {
            orderDetail.items && orderDetail.items.length > 0 &&
            <div className={`w-full bg-white rounded-[4px] ${isMobile ? 'p-[16px] flex items-center' : 'p-[24px]'}`}>
              <div className="body__item__content purchased">
                <div className={`text-16 font-700 ${!isMobile && 'mb-[16px]'}`}>{translate('purchased-product')}</div>

                {
                  !isMobile &&
                  <div className="purchased__board">
                    <Card
                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                      onResizeCapture={() => { }}
                      className="h-full w-full overflow-scroll overflow-y-hidden" placeholder="Card name table">
                      <table className="w-full min-w-max table-auto text-left">
                        <thead className="bg-[#e9ecff]">
                          <tr>
                            {TABLE_HEAD.map((head) => (
                              <th
                                key={head}
                                className="p-4"
                              >
                                <Typography
                                  variant="small"
                                  className="!font-600 text-14"
                                  placeholder="Typography"
                                  onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                  onResizeCapture={() => { }}
                                >
                                  {head}
                                </Typography>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {
                            orderDetail.items.length > 0 &&
                            orderDetail.items.map((item: any, index: number) => {
                              const isLast = index === orderDetail.items.length - 1;
                              const classes = isLast ? "p-4" : "p-4 border-b border-blue-gray-50";

                              return (
                                <tr key={`event_${item.id}_${index}`}>
                                  <td className={classes}>
                                    <Typography
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                    >
                                      {index + 1}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal order-code"
                                      placeholder="Typography"
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                    >
                                      {get(item, `product_name_${language}`, '')}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {item.quantity}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      đ {item.product_price.toLocaleString('de-DE')}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      đ {item.discount_amount.toLocaleString('de-DE')}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      đ {item.grand_total.toLocaleString('de-DE')}
                                    </Typography>
                                  </td>
                                  <td className={classes}>

                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {
                                        orderDetail.coupons && orderDetail.coupons.length > 0 &&
                                        orderDetail.coupons.map((coupon: any, index: number) => {
                                          return (
                                            <span key={`coupon_${index}`}>{coupon}</span>
                                          )
                                        })
                                      }
                                    </Typography>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                }

              </div>
            </div>
          }

          {
            (isMobile && orderDetail.items && orderDetail.items.length > 0) &&
            <div className="w-full grid grid-cols-1 gap-[16px]">
              {
                orderDetail.items.map((item: any, index: number) => {
                  return (
                    <div className="w-full p-[16px] bg-white rounded-[8px]" key={`purchased__item__${index}`}>
                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('no')}</div>
                        <div className="flex-1 text-14">{index + 1}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('ticket-name')}</div>
                        <div className="flex-1 text-14">{get(item, `product_name_${language}`, '')}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('quantity')}</div>
                        <div className="flex-1 text-14">{item.quantity}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('price')}</div>
                        <div className="flex-1 text-14">đ {item.product_price.toLocaleString('de-DE')}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('discount')}</div>
                        <div className="flex-1 text-14">đ {item.discount_amount.toLocaleString('de-DE')}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('total')}</div>
                        <div className="flex-1 text-14">đ {item.grand_total.toLocaleString('de-DE')}</div>
                      </div>

                      <div className="w-full flex items-center gap-x-[12px]">
                        <div className="w-[100px] text-14 font-500">{translate('used-code')}</div>
                        <div className="flex-1 text-14">
                          {
                            orderDetail.coupons && orderDetail.coupons.length > 0 &&
                            orderDetail.coupons.map((coupon: any, index: number) => {
                              return (
                                <span key={`${coupon}_${index}`}>{coupon}</span>
                              )
                            })
                          }
                        </div>
                      </div>
                    </div>
                  )
                })
              }
            </div>
          }
        </div>
      }

      <div className="w-[100%] h-[84px] bg-white sticky bottom-0 flex justify-end items-center">
        <a href={`/orders/${orderID}`}>
          <div className="w-[88px] h-[48px] flex items-center justify-center border-[1px] border-[#E9ECFF] rounded-[4px] text-[#011BB6] mr-[12px] text-[14px] cursor-pointer">{translate('btn-cancel')}</div>
        </a>

        <div onClick={handleUpdateOrder} className="w-[88px] h-[48px] flex items-center justify-center border-[1px] border-[#011BB6] bg-[#011BB6] rounded-[4px] text-white mr-[24px] text-[14px] cursor-pointer">{translate('btn-save')}</div>

      </div>

      {
        loading &&
        <FullScreenLoading />
      }
    </div>
  )
}

const WPSEditOrder = () => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState(false);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const parse = useParse();
  const { params } = parse();
  const [orderID, setOrderID] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  useEffect(() => {
    if (params && params?.id && params?.id !== '') {
      setOrderID(params?.id);
    }
  }, [params]);

  const breadcrumbItems = [
    {
      label: translate('orders-title'),
      href: '/orders'
    },
    {
      label: translate('edit-order'),
      href: ''
    },
  ];

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      <Breadcrumbs type="wps" breadcrumbItems={breadcrumbItems} disableBorder={true} />

      {
        (orderID !== '' && token !== '') &&
        <div className='w-full'>
          <OrderForm
            isMobile={isMobile}
            translate={translate}
            language={language}
            orderID={orderID}
            token={token} />
        </div>
      }

    </div>
  )
}

export default WPSEditOrder;